import { Card, Divider, Highlight, Typography, Body, Stack, StackItem } from '@optimus-web/core';
import { PayoutDetailSummary } from '@mews-commander/data-access';
import {
    BankAccountName,
    Dash,
    LocalizedDateTimeFromUtc,
    LocalizedLabel,
    LocalizedSmallParagraph,
} from '@mews-commander/ui';
import { LocalizedText, LocalizedDate, DateTimeService } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { getLocalizedCurrencyValue } from '../utils';
import { PayoutStateTag } from './PayoutStateTag';

export const PayoutReportDetailSummary = ({
    payoutDetailSummary: {
        net,
        reportingIdentifier,
        status,
        bankAccount,
        gross,
        commission,
        createdUtc,
        estimatedArrivalDate,
    },
}: {
    payoutDetailSummary: PayoutDetailSummary;
}) => (
    <Stack>
        <Card padding="200">
            <Stack vertical spacing="200">
                <Stack vertical spacing="200">
                    <Stack spacing="400">
                        <Stack vertical spacing="100">
                            <Typography textStyle="bodyMediumStrong" as="div">
                                {net.currencyCode}
                                {UnicodeEntity.NoBreakSpace}
                                <LocalizedText textKey={TextKey.PayoutNetPaidOut} />
                            </Typography>
                            <Highlight size="small">{getLocalizedCurrencyValue(net)}</Highlight>
                        </Stack>
                        <Stack vertical distribution="space-between">
                            <LocalizedLabel textKey={TextKey.Status} as="div" />
                            {status ? <PayoutStateTag state={status} /> : <Dash />}
                        </Stack>
                        <Stack vertical distribution="space-between">
                            <LocalizedLabel textKey={TextKey.PayoutId} as="div" />
                            <Body size="small">{reportingIdentifier}</Body>
                        </Stack>
                        <Stack vertical distribution="space-between">
                            <LocalizedLabel textKey={TextKey.BankAccount} as="div" />
                            <Body size="small">
                                {bankAccount ? <BankAccountName bankAccount={bankAccount} /> : <Dash />}
                            </Body>
                        </Stack>
                    </Stack>
                    <Divider />
                </Stack>
                <Stack spacing="200" distribution="stretch">
                    <Stack distribution="space-between" spacing="200">
                        <StackItem grow>
                            <Stack spacing="100" vertical>
                                <LocalizedLabel textKey={TextKey.Breakdown} as="div" />
                                <Stack distribution="space-between">
                                    <LocalizedSmallParagraph textKey={TextKey.PayoutGross} variant="secondary" />
                                    <Body size="small"> {getLocalizedCurrencyValue(gross)}</Body>
                                </Stack>
                                <Stack distribution="space-between">
                                    <LocalizedSmallParagraph textKey={TextKey.PayoutCommission} variant="secondary" />
                                    <Body size="small"> {getLocalizedCurrencyValue(commission)}</Body>
                                </Stack>
                            </Stack>
                        </StackItem>
                        <Divider direction="vertical" />
                    </Stack>
                    <Stack distribution="stretch">
                        <Stack spacing="100" vertical>
                            <LocalizedLabel textKey={TextKey.PayoutDates} as="div" />
                            <Stack distribution="space-between">
                                <LocalizedSmallParagraph textKey={TextKey.Created} variant="secondary" />
                                <Body size="small">
                                    <LocalizedDateTimeFromUtc utcString={createdUtc} />
                                </Body>
                            </Stack>
                            <Stack distribution="space-between">
                                <LocalizedSmallParagraph textKey={TextKey.EstimatedArrival} variant="secondary" />
                                <Body size="small">
                                    {estimatedArrivalDate ? (
                                        <LocalizedDate
                                            date={DateTimeService.fromLocalUtcString(estimatedArrivalDate)}
                                        />
                                    ) : (
                                        <Dash />
                                    )}
                                </Body>
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Card>
    </Stack>
);
